import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Eyebrow, MaskedLines, Reveal } from "../motion/Reveal";
import { EnquiryForm } from "../forms/EnquiryForm";
import { contact } from "../../content/site";

const steps = [
  ["Tell us what you need", "Choose a quote, site survey, consultation or general enquiry."],
  ["Receive a reference", "Every request is logged with a unique reference number."],
  ["We follow up", "The team reviews your request and gets back to you."],
];

const rowCls = "group flex items-center gap-4 border-t border-stone-300 py-4 transition-colors duration-200 hover:text-orange-700";
const iconCls = "grid h-10 w-10 shrink-0 place-items-center rounded-full border border-orange-600/40 text-orange-700 transition-colors duration-200 group-hover:bg-orange-600 group-hover:text-white";
const actionCls = "inline-flex h-9 items-center gap-1.5 rounded-full border px-4 text-xs font-semibold outline-none transition-colors duration-200 focus-visible:ring-4 focus-visible:ring-orange-600/20";

const PhoneRow = ({ p }) => (
  <li className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-stone-300 py-4" data-testid={`contact-phone-row-${p.id}`}>
    <span className={iconCls.replace(" group-hover:bg-orange-600 group-hover:text-white", "")}><Phone className="h-4 w-4" /></span>
    <span className="min-w-0">
      <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">{p.region} · Phone / WhatsApp</span>
      <span className="block whitespace-nowrap font-display text-lg text-stone-900">{p.display}</span>
    </span>
    <span className="ml-14 flex basis-full gap-2">
      <a href={`tel:${p.tel}`} data-testid={`contact-phone-${p.id}`} aria-label={`Call TechCare, ${p.region}: ${p.display}`}
        className={`${actionCls} border-stone-900 bg-stone-900 text-white hover:border-orange-600 hover:bg-orange-600`}>
        <Phone className="h-3.5 w-3.5" /> Call
      </a>
      <a href={p.whatsapp} target="_blank" rel="noopener noreferrer" data-testid={`contact-whatsapp-${p.id}`} aria-label={`WhatsApp TechCare, ${p.region}: ${p.display} (opens WhatsApp)`}
        className={`${actionCls} border-orange-600/50 text-orange-800 hover:border-orange-600 hover:bg-orange-600 hover:text-white`}>
        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
      </a>
    </span>
  </li>
);

const DirectContact = () => (
  <div className="mt-10 max-w-lg border-b border-stone-300" data-testid="contact-direct">
    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-stone-600">Talk to the team</p>
    <ul className="mt-4">
      {contact.phones.map((p) => <PhoneRow key={p.id} p={p} />)}
      <li>
        <a href={`mailto:${contact.email}`} className={rowCls} data-testid="contact-email-link">
          <span className={iconCls}><Mail className="h-4 w-4" /></span>
          <span className="min-w-0">
            <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Email</span>
            <span className="block break-all font-display text-lg text-stone-900 group-hover:text-orange-700">{contact.email}</span>
          </span>
        </a>
      </li>
      <li className="flex items-start gap-4 border-t border-stone-300 py-4" data-testid="contact-address">
        <span className={iconCls.replace(" group-hover:bg-orange-600 group-hover:text-white", "")}><MapPin className="h-4 w-4" /></span>
        <address className="min-w-0 not-italic">
          <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">{contact.address.label}</span>
          <span className="mt-0.5 block font-display text-lg leading-snug text-stone-900">
            {contact.address.lines.map((l) => <span key={l} className="block">{l}</span>)}
          </span>
        </address>
      </li>
    </ul>
    <p className="pb-4 pt-1 text-xs leading-relaxed text-stone-600" data-testid="contact-direct-note">
      The Australian number is an international contact line. WhatsApp links open a chat in WhatsApp; our team replies personally.
      For guided help, open the Concierge at the bottom of the screen.
    </p>
  </div>
);

export const ContactSection = ({ id = "contact", eyebrow = "09 — Contact", defaultService, defaultType }) => (
  <section id={id} data-testid="contact-section" className="tc-sheet bg-[#FAFAF9] py-24 text-stone-900 sm:py-32">
    <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
      <div className="lg:col-span-5">
        <Eyebrow light>{eyebrow}</Eyebrow>
        <h2 className="mt-8 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-6xl">
          <MaskedLines inView lines={["Let's take care", "of the tech."]} />
        </h2>
        <p className="mt-8 max-w-md text-base leading-relaxed text-stone-600">
          Request a quote, book a site survey or simply ask a question. Use the form for a tracked request with a reference number, or call or email the team directly.
        </p>
        <DirectContact />
        <ol className="mt-12 space-y-6">
          {steps.map(([t, d], i) => (
            <Reveal as="li" key={t} delay={i * 0.08} className="flex gap-5 border-t border-stone-300 pt-6">
              <span className="font-mono text-xs text-orange-700">0{i + 1}</span>
              <span>
                <span className="block font-display text-lg">{t}</span>
                <span className="mt-1 block text-sm text-stone-600">{d}</span>
              </span>
            </Reveal>
          ))}
        </ol>
      </div>
      <div className="relative lg:col-span-7">
        <EnquiryForm defaultService={defaultService} defaultType={defaultType} />
      </div>
    </div>
  </section>
);
